import {
  Injectable,
  InjectionToken,
  Signal,
  inject,
  signal,
} from '@angular/core';

export type CallSessionState = 'ringing' | 'answered' | 'ended';

export interface CallSessionPayload {
  callId: string;
  phone: string;
  location?: string;
  at: string;
  direction: 'inbound' | 'outbound';
  displayName?: string;
}

export interface CallSessionEvent extends CallSessionPayload {
  state: CallSessionState;
}

/**
 * Browser-safe telephony boundary. A future server-backed implementation can
 * replace this port without changing the call panel.
 */
export interface OutboundCallRequest {
  phone: string;
  callId?: string;
}

export interface CallSession {
  readonly session: Signal<CallSessionEvent>;
  startOutbound(request: OutboundCallRequest): void;
  answer(callId: string): void;
  end(callId: string): void;
}

const DEFAULT_INCOMING_CALL: Omit<CallSessionPayload, 'at'> = {
  callId: 'CALL-DEMO-001',
  phone: '(703) 555-0123',
  location: 'Richmond, VA',
  direction: 'inbound',
  displayName: 'Rosa Mendez',
};

function demoIncomingCall(at = new Date().toISOString()): CallSessionPayload {
  return { ...DEFAULT_INCOMING_CALL, at };
}

@Injectable({ providedIn: 'root' })
export class FakeCallAdapter implements CallSession {
  private readonly currentSession = signal<CallSessionEvent>({
    ...DEFAULT_INCOMING_CALL,
    at: '',
    state: 'ended',
  });

  readonly session = this.currentSession.asReadonly();

  simulateIncoming(payload: CallSessionPayload = demoIncomingCall()): void {
    this.currentSession.set({ ...payload, direction: payload.direction ?? 'inbound', state: 'ringing' });
  }

  startOutbound({ phone, callId }: OutboundCallRequest): void {
    this.currentSession.set({
      callId: callId ?? `CALL-OUT-${Date.now()}`,
      phone,
      at: new Date().toISOString(),
      direction: 'outbound',
      state: 'answered',
    });
  }

  answer(callId: string): void {
    this.updateState(callId, 'answered');
  }

  end(callId: string): void {
    this.updateState(callId, 'ended');
  }

  private updateState(callId: string, state: CallSessionState): void {
    this.currentSession.update((session) =>
      session.callId === callId ? { ...session, state } : session,
    );
  }
}

export const CALL_SESSION = new InjectionToken<CallSession>('CallSession', {
  providedIn: 'root',
  factory: () => inject(FakeCallAdapter),
});
